import React, { useState, useRef, useEffect } from 'react';
import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/20/solid';

export interface MultiSelectOption {
  id?: string | null;
  name?: string | null;
}

interface MultiSelectProps<T extends MultiSelectOption> {
  options: T[];
  value: T[];
  onChange: (value: T[]) => void;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  emptyText?: string;
  error?: boolean;
  showSelectAllControls?: boolean;
}

function MultiSelect<T extends MultiSelectOption>({
  options,
  value,
  onChange,
  placeholder = 'Select options',
  disabled = false,
  isLoading = false,
  loadingText = 'Loading...',
  emptyText = 'No options available',
  error = false,
  showSelectAllControls = false,
}: MultiSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter out options with missing id or name
  const validOptions = options.filter((opt) => opt.id && opt.name);

  const isSelected = (option: T) => value.some((v) => v.id === option.id);

  const handleToggle = (option: T) => {
    if (isSelected(option)) {
      onChange(value.filter((v) => v.id !== option.id));
    } else {
      onChange([...value, option]);
    }
  };

  const handleRemove = (option: T, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v.id !== option.id));
  };

  const allOptionsSelected =
    validOptions.length > 0 &&
    validOptions.every((option) =>
      value.some((selected) => selected.id === option.id),
    );

  const handleSelectAll = () => {
    onChange([...validOptions]);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`relative w-full cursor-default rounded-md border bg-white py-2 pr-10 pl-3 text-left shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 ${
          error ? 'border-red-300' : 'border-gray-300'
        }`}
      >
        <div className="flex flex-wrap gap-1">
          {value.length === 0 ? (
            <span className="text-gray-400">{placeholder}</span>
          ) : (
            value.map((item) => (
              <span
                key={item.id}
                className="inline-flex items-center gap-1 rounded-md bg-blue-100 px-2 py-0.5 text-sm text-blue-800"
              >
                {item.name}
                <button
                  type="button"
                  onClick={(e) => handleRemove(item, e)}
                  className="rounded-full p-0.5 hover:bg-blue-200 focus:outline-none"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </span>
            ))
          )}
        </div>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronUpDownIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </span>
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
          {isLoading ? (
            <div className="px-4 py-2 text-gray-500">{loadingText}</div>
          ) : validOptions.length === 0 ? (
            <div className="px-4 py-2 text-gray-500">{emptyText}</div>
          ) : (
            <>
              {showSelectAllControls ? (
                <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2">
                  <label className="flex cursor-pointer items-center text-sm">
                    <input
                      type="checkbox"
                      checked={allOptionsSelected}
                      onChange={(event) =>
                        event.target.checked
                          ? handleSelectAll()
                          : handleClearAll()
                      }
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 font-medium text-gray-900">
                      Select all
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={handleClearAll}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700"
                  >
                    Clear
                  </button>
                </div>
              ) : null}
              {validOptions.map((option) => (
                <label
                  key={option.id}
                  className={`flex cursor-pointer items-center px-4 py-2 hover:bg-blue-50 ${
                    isSelected(option) ? 'bg-blue-50' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected(option)}
                    onChange={() => handleToggle(option)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span
                    className={`ml-3 block truncate ${
                      isSelected(option)
                        ? 'font-medium text-blue-900'
                        : 'text-gray-900'
                    }`}
                  >
                    {option.name}
                  </span>
                </label>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default MultiSelect;
